import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cents } from "./money";
import { VatBasis } from "./price";

/**
 * Shipping rules (Instellingen → Verzendregels, CONTEXT.md `shippingRule`): per delivery country
 * and customer group a rate and a free-from threshold, in cents on the group's VAT basis (incl VAT
 * for Consument, excl VAT for business groups). A country and group without a rule: the shop does
 * not ship there for that group.
 *
 * The page loads `list` and saves every rule at once with `replace`, which answers the same
 * `ShippingRuleSettings`.
 */

/** The countries the shop ships to, in the order the page shows them. More come later. */
export const SHIPPING_COUNTRIES = ["NL", "BE"] as const;
export const ShippingCountry = z.enum(SHIPPING_COUNTRIES);
export type ShippingCountry = z.infer<typeof ShippingCountry>;

/** The most a rate or threshold may be: €10.000. A typo guard, not a business rule. */
const MAX_AMOUNT = 1_000_000;

export const ShippingRule = z.object({
	country: ShippingCountry,
	customerGroupId: z.string().min(1),
	/** What shipping costs, on the group's basis. */
	rate: Cents.min(0).max(MAX_AMOUNT),
	/** From this goods total (after discount, on the group's basis) shipping is free; `null`: never. */
	freeFrom: Cents.min(0).max(MAX_AMOUNT).nullable(),
});
export type ShippingRule = z.infer<typeof ShippingRule>;

/** A customer group as the page shows it: its rates are on `vatBasis`. */
export const ShippingCustomerGroup = z.object({
	id: z.string(),
	name: z.string(),
	vatBasis: VatBasis,
});
export type ShippingCustomerGroup = z.infer<typeof ShippingCustomerGroup>;

export const ShippingRuleSettings = z.object({
	countries: z.array(ShippingCountry),
	/** Consument first, then the business groups by name. */
	customerGroups: z.array(ShippingCustomerGroup),
	/** By country (in `countries` order), then by group (in `customerGroups` order). */
	rules: z.array(ShippingRule),
});
export type ShippingRuleSettings = z.infer<typeof ShippingRuleSettings>;

/** Every rule the shop has after the save: a country and group left out has no rule any more. */
export const ShippingRulesBody = z.object({
	rules: z
		.array(ShippingRule)
		.max(SHIPPING_COUNTRIES.length * 100)
		.refine(
			(rules) => new Set(rules.map((r) => `${r.country} ${r.customerGroupId}`)).size === rules.length,
			"One rule per country and customer group",
		),
});
export type ShippingRulesBody = z.infer<typeof ShippingRulesBody>;

/**
 * One country's shipping cost as the shop shows it (product page, the page about shipping): the
 * rule the cart charges this visitor. For a guest, a consumer or a business customer not yet
 * approved that is the Consument rule, incl VAT; for a signed-in approved business customer their
 * customer group's own rule, excl VAT, or Consument's where the group has none. `vatBasis` says
 * which. `freeFrom` is there only when a threshold is set (v1 has none).
 */
export const ShopShippingRate = z.object({
	country: ShippingCountry,
	rate: Cents,
	freeFrom: Cents.optional(),
	vatBasis: VatBasis,
});
export type ShopShippingRate = z.infer<typeof ShopShippingRate>;

/** The countries the shop ships to for this visitor, in `SHIPPING_COUNTRIES` order. */
export const ShopShipping = z.object({ countries: z.array(ShopShippingRate) });
export type ShopShipping = z.infer<typeof ShopShipping>;

/**
 * `list` and `replace` are the admin's; `shop` is public: cacheable (a few minutes) without a
 * customer session, private with one.
 * `replace` answers 422 `ShippingRuleCustomerGroupNotFound` for a group that does not
 * exist (deleted meanwhile).
 */
export const ShippingRuleContract = {
	list: oc.route({ method: "GET", path: "/admin/shipping-rules" }).output(ShippingRuleSettings),
	replace: oc
		.route({ method: "PUT", path: "/admin/shipping-rules" })
		.input(ShippingRulesBody)
		.output(ShippingRuleSettings),
	shop: oc.route({ method: "GET", path: "/shop/shipping" }).output(ShopShipping),
};
